// src/data/assignmentsHelpers.js
//
// Thin wrappers around the assignments/{studyId}_{reviewerId}_{paperId}
// collection introduced by ticket 07.
//
// Per the data-model doc §2.8, each doc records which subset of leaf
// questions a given reviewer is expected to answer on a given paper.
// Phase 1 writes one doc per (reviewer, paper) with questionIds set to
// the full leaf id list — i.e. "reviewer answers every visible question
// on this paper" — matching the pre-ticket-07 implicit behavior.
//
// Ticket 08 surfaces the per-paper subset in the admin matrix. Ticket 10
// is where the org-scoped doc id gets stamped with orgId and re-keyed.

import {
  collection,
  query,
  where,
  getDocs,
  doc,
  setDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

const ASSIGNMENTS = 'assignments';

/** Deterministic id for an (study, reviewer, paper) tuple. */
export function assignmentId(studyId, reviewerId, paperId) {
  return `${studyId}_${reviewerId}_${paperId}`;
}

/**
 * Returns all assignments for a given (study, reviewer) pair. Used by the
 * reviewer sidebar to know which papers they're expected to work on and
 * which subset of questions applies to each.
 */
export async function loadAssignmentsForReviewer(studyId, reviewerId) {
  if (!studyId || !reviewerId) return [];
  const q = query(
    collection(db, ASSIGNMENTS),
    where('studyId', '==', studyId),
    where('reviewerId', '==', reviewerId),
  );
  const snap = await getDocs(q);
  const out = [];
  snap.forEach((d) => out.push({ id: d.id, ...d.data() }));
  return out;
}

/**
 * Returns every assignment in a study. Used by the admin matrix so we
 * can render the full reviewer × paper grid in one Firestore read.
 */
export async function loadAssignmentsForStudy(studyId) {
  if (!studyId) return [];
  const q = query(collection(db, ASSIGNMENTS), where('studyId', '==', studyId));
  const snap = await getDocs(q);
  const out = [];
  snap.forEach((d) => out.push({ id: d.id, ...d.data() }));
  return out;
}

/**
 * Upserts an assignment with the given questionId subset. `createdBy` is
 * required on create so we have an audit trail; subsequent updates just
 * bump updatedAt.
 */
export async function upsertAssignment({
  studyId,
  reviewerId,
  paperId,
  orgId,
  questionIds,
  createdBy,
}) {
  if (!studyId || !reviewerId || !paperId) {
    throw new Error('upsertAssignment requires studyId, reviewerId, paperId');
  }
  const id = assignmentId(studyId, reviewerId, paperId);
  const ref = doc(db, ASSIGNMENTS, id);
  const now = serverTimestamp();
  await setDoc(
    ref,
    {
      orgId: orgId || null,
      studyId,
      reviewerId,
      paperId,
      questionIds: Array.isArray(questionIds) ? questionIds : [],
      createdAt: now,
      createdBy: createdBy || 'system',
      updatedAt: now,
    },
    { merge: true },
  );
  return id;
}

/**
 * Deletes an assignment entirely. Used when an admin unassigns a paper
 * from a reviewer — the ticket-08 matrix editor also calls this when
 * the question subset is saved empty.
 */
export async function removeAssignment({ studyId, reviewerId, paperId }) {
  const id = assignmentId(studyId, reviewerId, paperId);
  await deleteDoc(doc(db, ASSIGNMENTS, id));
}
